import { useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';

import { FormControl, FormHelperText, Stack, TextField, styled } from '@mui/material';

export const CODE_LENGTH = 6;
const DIGIT_REGEX = /^\d$/;

const CodeInputWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  justifyContent: 'center',

  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(2),
  },
}));

const CodeInputTextField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    width: 56,
  },

  width: 42,
}));

type CodeInputProps = {
  errorMessage?: ReactNode | null;
  onCodeChange: (code: string) => void;
};

export const CodeInput = ({ errorMessage, onCodeChange }: CodeInputProps) => {
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.slice(-1);
    if (digit && !DIGIT_REGEX.test(digit)) return;

    const nextDigits = [...digits];
    nextDigits[index] = digit;
    setDigits(nextDigits);

    onCodeChange(nextDigits.join(''));
    if (digit && index < CODE_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleDigitKeyDown = (index: number, event: KeyboardEvent) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pastedText) return;

    const newDigits = pastedText.split('');
    const fullDigits = [
      ...newDigits,
      ...Array(CODE_LENGTH - newDigits.length).fill(''),
    ] as string[];

    setDigits(fullDigits.slice(0, CODE_LENGTH));
    onCodeChange(fullDigits.slice(0, CODE_LENGTH).join(''));
    const nextFocusIndex = Math.min(pastedText.length, CODE_LENGTH - 1);
    setTimeout(() => inputRefs.current[nextFocusIndex]?.focus(), 0);
  };

  return (
    <FormControl error={Boolean(errorMessage)} fullWidth>
      <CodeInputWrapper>
        {Array.from({ length: CODE_LENGTH }, (_, index) => (
          <CodeInputTextField
            error={Boolean(errorMessage)}
            inputMode="numeric"
            inputProps={{
              'aria-label': `Digit ${index + 1}`,
              autoComplete: 'off',
              maxLength: 1,
              sx: { textAlign: 'center' },
            }}
            inputRef={(element) => {
              inputRefs.current[index] = element;
            }}
            key={index}
            onChange={(event) => handleDigitChange(index, event.target.value)}
            onFocus={(event) => event.target.setSelectionRange(0, event.target.value.length)}
            onKeyDown={(event) => handleDigitKeyDown(index, event)}
            onPaste={handlePaste}
            value={digits[index]}
          />
        ))}
      </CodeInputWrapper>

      {errorMessage && (
        <FormHelperText error sx={{ marginLeft: 0 }}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
};
